import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  List,
  message,
  Modal,
  Row,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  CreditCardOutlined,
  ExportOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import paymentMethodsService from "services/paymentMethodsService";
import useAppConfig from "hooks/access-control/useAppConfig";
import {
  organizationName,
  readable,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceStyles } from "./colors";

const { Text } = Typography;

const STRIPE_JS_URL = "https://js.stripe.com/v3/";
let stripeJsPromise;

const loadStripeJs = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Stripe can only be loaded in the browser."));
  }
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;

  stripeJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", () => reject(new Error("Stripe.js failed to load.")));
      return;
    }

    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => reject(new Error("Stripe.js failed to load."));
    document.body.appendChild(script);
  });

  return stripeJsPromise;
};

const stripeCardOptions = {
  hidePostalCode: false,
  style: {
    base: {
      color: "#0F172A",
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#94A3B8",
      },
    },
    invalid: {
      color: "#D9291C",
      iconColor: "#D9291C",
    },
  },
};

const sectionTitle = (title) => (
  <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
    {title}
  </Text>
);

const paymentMethodId = (method) =>
  method?.id || method?.stripePaymentMethodId || method?.paymentMethodId || "";

const maskedMethod = (method) => {
  const brand = method?.merchant || method?.brand || method?.cardBrand || "Card";
  const last4 = method?.last4 || method?.cardLast4 || method?.accountLast4;
  return last4 ? `${brand} •••• ${last4}` : brand;
};

const expiry = (method) => {
  const month = method?.expiryMonth || method?.expMonth;
  const year = method?.expiryYear || method?.expYear;
  if (!month || !year) return "-";
  return `${String(month).padStart(2, "0")}/${year}`;
};

const isDefaultOutgoing = (method) =>
  Boolean(method?.defaultOutgoingPaymentMethod || method?.isDefaultForCurrency);

const isDefaultIncoming = (method) =>
  Boolean(
    method?.defaultIncomingPaymentMethod ||
      method?.isDefaultIncomingPaymentMethod ||
      method?.isDefaultForCurrency ||
      method?.isDefaultPaymentAccount,
  );

const payoutMethodId = (method) =>
  method?.id || method?.externalPayoutAccountId || method?.externalAccountId || "";

const maskedPayoutMethod = (method) => {
  const brand =
    method?.bankName ||
    method?.bank ||
    method?.institutionName ||
    method?.brand ||
    method?.accountHolderName ||
    method?.type ||
    "Payout account";
  const last4 = method?.last4 || method?.lastDigits || method?.last4Digits;
  return last4 ? `${brand} •••• ${last4}` : brand;
};

const boolValue = (value) => value === true || String(value).toLowerCase() === "true";

const boolLabel = (value) => (boolValue(value) ? "true" : "false");

const connectStripeAccountId = (account) =>
  account?.stripeAccountId ||
  account?.stripe_account_id ||
  account?.stripeConnectAccountId ||
  account?.accountId ||
  account?.connectedAccountId ||
  "";

const payoutGraphicCard = {
  borderRadius: 8,
  border: `1px solid ${OLIVEEB_COLORS.border}`,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.card} 0%, ${OLIVEEB_COLORS.greenSoft2} 100%)`,
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};

const statusTileStyle = (active) => ({
  borderRadius: 8,
  padding: "14px 16px",
  border: `1px solid ${active ? "rgba(27, 174, 66, 0.28)" : "rgba(217, 41, 28, 0.2)"}`,
  background: active
    ? "linear-gradient(135deg, rgba(27, 174, 66, 0.13), rgba(0, 136, 204, 0.08))"
    : "linear-gradient(135deg, rgba(255, 246, 214, 0.9), rgba(255, 255, 255, 0.96))",
  minHeight: 92,
});

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const isHelperWorkspace = (workspace) => {
  const use = String(
    workspace?.workspaceUse ||
      workspace?.workspaceUseName ||
      workspace?.use ||
      workspace?.useType ||
      "",
  ).toUpperCase();
  return use === "HELPER" || use === "HIRING";
};

const activeAccountUser = (account, fallbackUser) => {
  const users = account?.users || [];
  const active = users.find((member) => {
    const status = String(member?.membershipStatus || "").toUpperCase();
    return status === "ACTIVE" || status === "ACCEPTED" || status === "";
  });
  return active?.user || fallbackUser || {};
};

const userName = (user, fallback = "User") => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || user?.id || fallback;
};

const paymentContext = (account, fallbackUser, fallbackUserId, organization) => {
  if (!account) {
    return {
      type: "workspace",
      label: "Workspace",
      detail: "-",
      userId: fallbackUserId,
      organizationId: "",
      canManageMethods: false,
    };
  }

  if (isPersonalAccount(account)) {
    const ownerUser = activeAccountUser(account, fallbackUser);
    const ownerUserId = ownerUser?.id || fallbackUserId;
    return {
      type: "user",
      label: "Personal user",
      detail: userName(ownerUser, ownerUserId || "User"),
      userId: ownerUserId,
      organizationId: "",
      canManageMethods: Boolean(ownerUserId),
    };
  }

  return {
    type: "business",
    label: "Organization",
    detail: account.organizationId
      ? organizationName(organization, account.organizationId)
      : "Not linked",
    userId: "",
    organizationId: account.organizationId || "",
    canManageMethods: Boolean(account.organizationId),
  };
};

const WorkspacePaymentMethodsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [outgoingMethods, setOutgoingMethods] = useState([]);
  const [incomingMethods, setIncomingMethods] = useState([]);
  const [connectAccount, setConnectAccount] = useState(null);
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [activeTab, setActiveTab] = useState("incoming");
  const [loading, setLoading] = useState(false);
  const [refreshingConnect, setRefreshingConnect] = useState(false);
  const [openingStripe, setOpeningStripe] = useState(false);
  const [defaultingId, setDefaultingId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addingMethod, setAddingMethod] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const stripeRef = useRef(null);
  const stripeCardRef = useRef(null);
  const cardMountRef = useRef(null);
  const stripePublishableKey =
    appConfig?.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      let nextOrganization = null;
      if (!isPersonalAccount(nextAccount) && nextAccount.organizationId) {
        try {
          nextOrganization = await olreadiaService.organizations.get(
            nextAccount.organizationId,
          );
        } catch (error) {
          nextOrganization = null;
        }
      }
      setOrganization(nextOrganization);
      const helperWorkspace = isHelperWorkspace(nextAccount);
      if (!helperWorkspace) {
        setActiveTab("outgoing");
      }

      if (isPersonalAccount(nextAccount)) {
        const ownerUser = activeAccountUser(nextAccount, user);
        const ownerUserId = ownerUser?.id || actorId;
        const [outgoing, incoming, nextConnectAccount] = await Promise.all([
          paymentMethodsService.outgoing.list({
            actorId,
            userId: ownerUserId,
            workspaceId,
            page: 0,
            size: 50,
          }),
          helperWorkspace
            ? paymentMethodsService.incoming.list({
                actorId,
                userId: ownerUserId,
                workspaceId,
              }).catch(() => [])
            : Promise.resolve([]),
          helperWorkspace
            ? paymentMethodsService.incoming.getConnectAccount({
                actorId,
                userId: ownerUserId,
                workspaceId,
                refresh: false,
              }).catch(() => null)
            : Promise.resolve(null),
        ]);
        setOutgoingMethods(outgoing);
        setIncomingMethods(incoming);
        setConnectAccount(nextConnectAccount);
        return;
      }

      if (!nextAccount.organizationId) {
        setOutgoingMethods([]);
        setIncomingMethods([]);
        setConnectAccount(null);
        return;
      }

      const [outgoing, incoming, nextConnectAccount] = await Promise.all([
        paymentMethodsService.outgoing.list({
          actorId,
          organizationId: nextAccount.organizationId,
          workspaceId,
          page: 0,
          size: 50,
        }),
        helperWorkspace
          ? paymentMethodsService.incoming.list({
              actorId,
              organizationId: nextAccount.organizationId,
              workspaceId,
            }).catch(() => [])
          : Promise.resolve([]),
        helperWorkspace
          ? paymentMethodsService.incoming.getConnectAccount({
              actorId,
              userId: actorId,
              workspaceId,
              refresh: false,
            }).catch(() => null)
          : Promise.resolve(null),
      ]);
      setOutgoingMethods(outgoing);
      setIncomingMethods(incoming);
      setConnectAccount(nextConnectAccount);
    } catch (error) {
      message.error(error?.message || "Failed to load payment methods.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!addOpen) return undefined;

    let active = true;
    setStripeReady(false);
    setStripeError("");
    setCardComplete(false);

    if (!stripePublishableKey) {
      setStripeError("Stripe publishable key is not configured.");
      return undefined;
    }

    loadStripeJs()
      .then((Stripe) => {
        if (!active) return;
        const stripe = Stripe(stripePublishableKey);
        const elements = stripe.elements();
        const card = elements.create("card", stripeCardOptions);
        stripeRef.current = stripe;
        stripeCardRef.current = card;

        card.on("change", (event) => {
          setCardComplete(Boolean(event.complete));
          setStripeError(event.error?.message || "");
        });

        if (cardMountRef.current) {
          card.mount(cardMountRef.current);
          setStripeReady(true);
        }
      })
      .catch((error) => {
        if (active) setStripeError(error?.message || "Stripe.js failed to load.");
      });

    return () => {
      active = false;
      if (stripeCardRef.current) {
        stripeCardRef.current.destroy();
        stripeCardRef.current = null;
      }
      stripeRef.current = null;
    };
  }, [addOpen, stripePublishableKey]);

  const defaultOutgoing = useMemo(
    () => outgoingMethods.find((method) => isDefaultOutgoing(method)) || null,
    [outgoingMethods],
  );

  const defaultIncoming = useMemo(
    () => incomingMethods.find((method) => isDefaultIncoming(method)) || null,
    [incomingMethods],
  );

  const setDefaultOutgoing = async (method) => {
    const id = paymentMethodId(method);
    if (!id) {
      message.error("Payment method id is missing.");
      return;
    }

    try {
      setDefaultingId(id);
      const context = paymentContext(account, user, actorId);
      await paymentMethodsService.outgoing.setDefault({
        actorId,
        methodId: id,
        userId: context.userId,
        organizationId: context.organizationId,
        workspaceId,
      });
      message.success("Default payment method updated.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to update default payment method.");
    } finally {
      setDefaultingId("");
    }
  };

  const closeAddPaymentMethod = () => {
    if (addingMethod) return;
    setAddOpen(false);
    setCardComplete(false);
    setStripeError("");
  };

  const addPaymentMethod = async () => {
    if (!stripeRef.current || !stripeCardRef.current) {
      message.error("Stripe card entry is not ready yet.");
      return;
    }

    if (!cardComplete) {
      message.error("Enter the full card details first.");
      return;
    }

    try {
      setAddingMethod(true);
      const billingName = userName(user, "");
      const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
        type: "card",
        card: stripeCardRef.current,
        billing_details: {
          name: billingName || undefined,
          email: user?.email || undefined,
        },
      });

      if (error || !paymentMethod?.id) {
        setStripeError(error?.message || "Stripe could not create a payment method.");
        message.error(error?.message || "Failed to create payment method.");
        return;
      }

      const context = paymentContext(account, user, actorId);
      await paymentMethodsService.outgoing.attach({
        actorId,
        userId: context.userId,
        organizationId: context.organizationId,
        workspaceId,
        paymentMethodId: paymentMethod.id,
      });
      message.success("Payment method added.");
      setAddOpen(false);
      setCardComplete(false);
      setStripeError("");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to add payment method.");
    } finally {
      setAddingMethod(false);
    }
  };

  const context = paymentContext(account, user, actorId, organization);
  const helperWorkspace = isHelperWorkspace(account);
  const helperUserId = context.userId || actorId;
  const stripeAccountId = connectStripeAccountId(connectAccount);
  const isOnboarded = boolValue(connectAccount?.onboarded);
  const payoutsEnabled = connectAccount?.payoutsEnabled ?? connectAccount?.payouts_enabled;
  const detailsSubmitted = connectAccount?.detailsSubmitted ?? connectAccount?.details_submitted;
  const loadingPaymentSetup = loading || refreshingConnect;
  const hasAnyPayoutAccount = incomingMethods.length > 0;
  const helperPayoutSubtitle = loadingPaymentSetup
    ? "Checking Stripe..."
    : !stripeAccountId
      ? "Set up Stripe Connect."
      : !hasAnyPayoutAccount
        ? isOnboarded ? "Manage payout methods in Stripe." : "Complete Stripe onboarding."
        : !isOnboarded
          ? "Onboarding required."
          : "Manage payouts in Stripe.";

  const copyText = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied.`);
    } catch {
      message.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const refreshConnectAccount = async () => {
    if (!helperWorkspace || !workspaceId || !actorId) return;
    try {
      setRefreshingConnect(true);
      const [nextConnectAccount, incoming] = await Promise.all([
        paymentMethodsService.incoming.getConnectAccount({
          actorId,
          userId: helperUserId,
          workspaceId,
          refresh: true,
        }).catch(() => null),
        paymentMethodsService.incoming.list({
          actorId,
          userId: helperUserId,
          workspaceId,
        }).catch(() => []),
      ]);
      setConnectAccount(nextConnectAccount);
      setIncomingMethods(incoming);
    } catch (error) {
      message.error(error?.message || "We could not refresh your Stripe account right now.");
    } finally {
      setRefreshingConnect(false);
    }
  };

  const openStripeUrl = (url) => {
    if (!url) {
      message.warning("Stripe link is not available right now.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const completeOnboarding = async () => {
    try {
      setOpeningStripe(true);
      const result = await paymentMethodsService.incoming.createOnboardingLink({
        actorId,
        userId: helperUserId,
        workspaceId,
      });
      const url = result?.url || result;
      setOnboardingUrl(url || "");
      openStripeUrl(url);
    } catch (error) {
      message.error(error?.message || "We could not start Stripe onboarding right now.");
    } finally {
      setOpeningStripe(false);
    }
  };

  const openDashboard = async () => {
    try {
      setOpeningStripe(true);
      const result = await paymentMethodsService.incoming.createDashboardLink({
        actorId,
        userId: helperUserId,
        workspaceId,
      });
      openStripeUrl(result?.url || result);
    } catch (error) {
      message.error(error?.message || "We could not open the Stripe dashboard right now.");
    } finally {
      setOpeningStripe(false);
    }
  };

  return (
    <div className="project-page">
      <Card
        style={{
          ...payoutGraphicCard,
          marginTop: 12,
          marginBottom: 16,
          background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkGreen}, ${OLIVEEB_COLORS.oliveebGreen} 54%, ${OLIVEEB_COLORS.oliveebBlue})`,
        }}
        styles={{ body: { padding: 20 } }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <Space align="center" size={14}>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: 8,
                display: "grid",
                placeItems: "center",
                background: "rgba(255, 255, 255, 0.16)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 16px 34px rgba(0, 0, 0, 0.16)",
              }}
            >
              <CreditCardOutlined style={{ color: OLIVEEB_COLORS.white, fontSize: 27 }} />
            </div>
            <div>
              <Text style={{ display: "block", color: "rgba(255, 255, 255, 0.78)", fontWeight: 800 }}>
                Workspace wallet
              </Text>
              <Text strong style={{ display: "block", color: OLIVEEB_COLORS.white, fontSize: 28, lineHeight: 1.1 }}>
                {helperWorkspace ? "Payouts and charge methods" : "Charge payment methods"}
              </Text>
              <Text style={{ display: "block", color: "rgba(255, 255, 255, 0.82)", marginTop: 4 }}>
                {helperWorkspace
                  ? "Manage how this workspace receives earnings and pays for charges."
                  : "Manage how this workspace pays for requests and other charges."}
              </Text>
            </div>
          </Space>
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.workspaceDetails(workspaceId))}>
              Back
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={loadData}
              style={{
                borderColor: "rgba(255, 255, 255, 0.7)",
                background: "rgba(255, 255, 255, 0.16)",
                color: OLIVEEB_COLORS.white,
                fontWeight: 800,
              }}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </Card>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!context.canManageMethods ? (
            <Alert
              type="warning"
              showIcon
              message={
                !isPersonalAccount(account) && !account.organizationId ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 800 }}>
                    This workspace is not linked to an organization.
                  </Text>
                ) : (
                  "Payment methods cannot be managed for this workspace yet."
                )
              }
              description={
                isPersonalAccount(account) ? (
                  "A workspace user could not be resolved for this personal workspace."
                ) : (
                  <Space direction="vertical" size={8} align="start">
                    <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                      Payment methods can be added after an organization is linked to this workspace.
                    </Text>
                    <Button
                      size="small"
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                      style={{
                        background: OLIVEEB_COLORS.oliveebBlue,
                        borderColor: OLIVEEB_COLORS.oliveebBlue,
                        color: OLIVEEB_COLORS.white,
                        fontWeight: 800,
                      }}
                    >
                      Link organization
                    </Button>
                  </Space>
                )
              }
              style={{ textAlign: "left" }}
            />
          ) : null}

          <Card style={payoutGraphicCard}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <div style={statusTileStyle(true)}>
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">{context.label}</Text>
                    <Text strong style={{ fontSize: 18 }}>{context.detail || "Not linked"}</Text>
                    <Tag color={context.canManageMethods ? "green" : "gold"} style={{ width: "fit-content" }}>
                      {context.type}
                    </Tag>
                  </Space>
                </div>
              </Col>
              {helperWorkspace ? (
                <Col xs={24} md={8}>
                  <div style={statusTileStyle(Boolean(incomingMethods.length))}>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">Incoming / Payout</Text>
                      <Text strong style={{ fontSize: 24 }}>{incomingMethods.length}</Text>
                      <Tag color={incomingMethods.length ? "green" : "default"} style={{ width: "fit-content" }}>
                        receiving methods
                      </Tag>
                    </Space>
                  </div>
                </Col>
              ) : null}
              <Col xs={24} md={8}>
                <div style={statusTileStyle(Boolean(outgoingMethods.length))}>
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">Outgoing / Charges</Text>
                    <Text strong style={{ fontSize: 24 }}>{outgoingMethods.length}</Text>
                    <Tag color={outgoingMethods.length ? "blue" : "default"} style={{ width: "fit-content" }}>
                      charge methods
                    </Tag>
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {helperWorkspace ? (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                { key: "incoming", label: "Incoming / Earnings / Payout" },
                { key: "outgoing", label: "Outgoing / Charges" },
              ]}
            />
          ) : null}

          {helperWorkspace && activeTab === "incoming" ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card
                title={sectionTitle("Stripe Payout Setup")}
                extra={
                  <Space wrap>
                    <Button
                      icon={<ReloadOutlined />}
                      loading={refreshingConnect}
                      onClick={refreshConnectAccount}
                    >
                      Refresh
                    </Button>
                    {!loadingPaymentSetup && hasAnyPayoutAccount ? (
                      <Button
                        type="link"
                        icon={isOnboarded ? <ExportOutlined /> : <RocketOutlined />}
                        loading={openingStripe}
                        onClick={isOnboarded ? openDashboard : completeOnboarding}
                        style={{ color: isOnboarded ? "#1677ff" : OLIVEEB_COLORS.oliveebGreen }}
                      >
                        {isOnboarded ? "Dashboard" : "Onboard"}
                      </Button>
                    ) : null}
                  </Space>
                }
                style={payoutGraphicCard}
              >
                <div
                  className="d-flex justify-content-between align-items-center gap-3 flex-wrap"
                  style={{
                    marginBottom: 16,
                    padding: 16,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                    color: OLIVEEB_COLORS.white,
                  }}
                >
                  <Space align="center" size={12}>
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 8,
                        display: "grid",
                        placeItems: "center",
                        background: "rgba(255, 255, 255, 0.18)",
                        border: "1px solid rgba(255, 255, 255, 0.32)",
                      }}
                    >
                      <BankOutlined style={{ color: OLIVEEB_COLORS.white, fontSize: 22 }} />
                    </div>
                    <div>
                      <Text strong style={{ display: "block", color: OLIVEEB_COLORS.white, fontSize: 17 }}>
                        Receiving payments
                      </Text>
                      <Text style={{ display: "block", color: "rgba(255, 255, 255, 0.82)" }}>
                        {helperPayoutSubtitle}
                      </Text>
                    </div>
                  </Space>
                  <Space wrap>
                    <Tag color={stripeAccountId ? "success" : "warning"} style={{ marginInlineEnd: 0 }}>
                      {stripeAccountId ? "Connected" : "Not connected"}
                    </Tag>
                    <Tag color={hasAnyPayoutAccount ? "success" : "default"} style={{ marginInlineEnd: 0 }}>
                      {incomingMethods.length} method(s)
                    </Tag>
                  </Space>
                </div>

                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={8}>
                    <div style={statusTileStyle(Boolean(stripeAccountId))}>
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">Stripe account</Text>
                        <Text strong>{stripeAccountId ? "Connected" : "Not connected"}</Text>
                        <Tag color={stripeAccountId ? "green" : "gold"} style={{ width: "fit-content" }}>
                          {connectAccount?.status || "pending"}
                        </Tag>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div style={statusTileStyle(isOnboarded)}>
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">Onboarding</Text>
                        <Text strong>{isOnboarded ? "Complete" : "Required"}</Text>
                        <Tag color={isOnboarded ? "green" : "gold"} style={{ width: "fit-content" }}>
                          onboarded={boolLabel(connectAccount?.onboarded)}
                        </Tag>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div style={statusTileStyle(boolValue(payoutsEnabled))}>
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">Payouts</Text>
                        <Text strong>{boolValue(payoutsEnabled) ? "Enabled" : "Not enabled"}</Text>
                        <Tag color={boolValue(detailsSubmitted) ? "green" : "gold"} style={{ width: "fit-content" }}>
                          details_submitted={boolLabel(detailsSubmitted)}
                        </Tag>
                      </Space>
                    </div>
                  </Col>
                </Row>

                {loadingPaymentSetup ? (
                  <Card size="small" style={workspaceStyles.softCard}>
                    <Skeleton active paragraph={{ rows: 3 }} />
                  </Card>
                ) : connectAccount ? (
                  <Collapse
                    bordered={false}
                    defaultActiveKey={[]}
                    items={[
                      {
                        key: "connect-details",
                        label: (
                          <Space direction="vertical" size={2}>
                            <Text strong>Connected account details</Text>
                            <Text type="secondary">
                              {connectAccount?.status || "unknown"} · {stripeAccountId || "unknown account"}
                            </Text>
                          </Space>
                        ),
                        children: (
                          <Space direction="vertical" size={8} style={{ width: "100%" }}>
                            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                              <Text type="secondary">stripe_account_id</Text>
                              {stripeAccountId ? (
                                <Button
                                  type="link"
                                  icon={<CopyOutlined />}
                                  onClick={() => copyText(stripeAccountId, "Stripe account ID")}
                                  style={{ paddingInline: 0 }}
                                >
                                  {stripeAccountId}
                                </Button>
                              ) : (
                                <Text>unknown</Text>
                              )}
                            </div>
                            <div className="d-flex justify-content-between gap-3 flex-wrap">
                              <Text type="secondary">type</Text>
                              <Text>{String(connectAccount?.type || "unknown").toLowerCase()}</Text>
                            </div>
                            <div className="d-flex justify-content-between gap-3 flex-wrap">
                              <Text type="secondary">status</Text>
                              <Text>{connectAccount?.status || "unknown"}</Text>
                            </div>
                            <div className="d-flex justify-content-between gap-3 flex-wrap">
                              <Text type="secondary">onboarded</Text>
                              <Text type={boolValue(connectAccount?.onboarded) ? "success" : "warning"}>
                                {boolLabel(connectAccount?.onboarded)}
                              </Text>
                            </div>
                            <div className="d-flex justify-content-between gap-3 flex-wrap">
                              <Text type="secondary">payouts_enabled</Text>
                              <Text type={boolValue(payoutsEnabled) ? "success" : "warning"}>
                                {boolLabel(payoutsEnabled)}
                              </Text>
                            </div>
                            <div className="d-flex justify-content-between gap-3 flex-wrap">
                              <Text type="secondary">details_submitted</Text>
                              <Text type={boolValue(detailsSubmitted) ? "success" : "warning"}>
                                {boolLabel(detailsSubmitted)}
                              </Text>
                            </div>
                            {onboardingUrl ? (
                              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                                <Text type="secondary">onboarding_link</Text>
                                <Space>
                                  <Button
                                    type="link"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyText(onboardingUrl, "Onboarding link")}
                                    style={{ paddingInline: 0 }}
                                  >
                                    Copy link
                                  </Button>
                                  <Button
                                    type="link"
                                    icon={<ExportOutlined />}
                                    onClick={() => openStripeUrl(onboardingUrl)}
                                    style={{ paddingInline: 0, color: OLIVEEB_COLORS.oliveebGreen }}
                                  >
                                    Open
                                  </Button>
                                </Space>
                              </div>
                            ) : null}
                            {!onboardingUrl && !isOnboarded ? (
                              <Button
                                type="link"
                                icon={<LinkOutlined />}
                                loading={openingStripe}
                                onClick={completeOnboarding}
                                style={{ paddingInline: 0 }}
                              >
                                Generate onboarding link
                              </Button>
                            ) : null}
                          </Space>
                        ),
                      },
                    ]}
                  />
                ) : null}

                {!loadingPaymentSetup && !hasAnyPayoutAccount ? (
                  <Space direction="vertical" size={12} style={{ width: "100%", marginTop: connectAccount ? 16 : 0 }}>
                    <Text type="secondary">
                      {isOnboarded
                        ? "Manage payout methods in Stripe Dashboard."
                        : "Complete Stripe onboarding to manage payout methods."}
                    </Text>
                    <Button
                      type="primary"
                      icon={isOnboarded ? <ExportOutlined /> : <RocketOutlined />}
                      loading={openingStripe}
                      onClick={isOnboarded ? openDashboard : completeOnboarding}
                      style={{ background: OLIVEEB_COLORS.oliveebGreen, borderColor: OLIVEEB_COLORS.oliveebGreen }}
                    >
                      {isOnboarded
                        ? "Open Stripe Dashboard"
                        : stripeAccountId
                          ? "Complete Stripe onboarding"
                          : "Start Stripe onboarding"}
                    </Button>
                  </Space>
                ) : !loadingPaymentSetup && !isOnboarded ? (
                  <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 16 }}>
                    <Text type="secondary">Complete onboarding to enable payouts. Debit cards are added in Olitask.</Text>
                    <Button
                      type="primary"
                      icon={<RocketOutlined />}
                      loading={openingStripe}
                      onClick={completeOnboarding}
                      style={{ background: OLIVEEB_COLORS.oliveebGreen, borderColor: OLIVEEB_COLORS.oliveebGreen }}
                    >
                      Complete Onboarding
                    </Button>
                  </Space>
                ) : null}
              </Card>

              <Card
                title={sectionTitle("Your Payout Methods")}
                extra={<Tag color={defaultIncoming ? "green" : "gold"}>{helperPayoutSubtitle}</Tag>}
                style={payoutGraphicCard}
              >
                {loadingPaymentSetup ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : incomingMethods.length ? (
                  <List
                    dataSource={incomingMethods}
                    renderItem={(method) => {
                      const isDefault = isDefaultIncoming(method);

                      return (
                        <List.Item key={payoutMethodId(method)}>
                          <Card
                            size="small"
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              borderColor: isDefault
                                ? OLIVEEB_COLORS.oliveebGreen
                                : OLIVEEB_COLORS.border,
                              background: isDefault
                                ? "linear-gradient(135deg, rgba(27, 174, 66, 0.14), rgba(255, 255, 255, 0.98))"
                                : "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(240, 247, 255, 0.88))",
                              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                              <Space align="start" style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 8,
                                    display: "grid",
                                    placeItems: "center",
                                    background: isDefault
                                      ? "rgba(27, 174, 66, 0.16)"
                                      : "rgba(0, 136, 204, 0.12)",
                                    border: `1px solid ${isDefault ? "rgba(27, 174, 66, 0.25)" : "rgba(0, 136, 204, 0.2)"}`,
                                  }}
                                >
                                  <BankOutlined style={{ color: isDefault ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.oliveebBlue }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <Text strong style={{ display: "block", wordBreak: "break-word" }}>
                                    {maskedPayoutMethod(method)}
                                  </Text>
                                  <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                                    <Tag color="blue">Payout</Tag>
                                    {method?.currency ? (
                                      <Tag color="cyan">{String(method.currency).toUpperCase()}</Tag>
                                    ) : null}
                                    {method?.country ? <Tag color="geekblue">{method.country}</Tag> : null}
                                    {isDefault ? <Tag color="green">Default receiving</Tag> : null}
                                  </Space>
                                </div>
                              </Space>
                              <Tag color={isDefault ? "green" : "default"} style={{ marginInlineEnd: 0 }}>
                                {readable(method?.status, "Status")}
                              </Tag>
                            </div>
                          </Card>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No methods yet."
                  />
                )}
              </Card>
            </Space>
          ) : (
          <Card
            title={sectionTitle("Outgoing / Charges Payment Methods")}
            extra={
              <Space wrap>
                {defaultOutgoing ? (
                  <Tag color="green">{maskedMethod(defaultOutgoing)} default</Tag>
                ) : (
                  <Tag color="gold">No default</Tag>
                )}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!context.canManageMethods}
                  style={{
                    borderColor: OLIVEEB_COLORS.oliveebBlue,
                    background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebGreen})`,
                    boxShadow: "0 10px 22px rgba(0, 136, 204, 0.18)",
                  }}
                  onClick={() => setAddOpen(true)}
                >
                  Add Payment Method
                </Button>
              </Space>
            }
            style={{ borderRadius: 8 }}
          >
            {outgoingMethods.length ? (
              <List
                dataSource={outgoingMethods}
                renderItem={(method) => {
                  const id = paymentMethodId(method);
                  const isDefault = isDefaultOutgoing(method);

                  return (
                    <List.Item>
                      <Card
                        size="small"
                        style={{
                          width: "100%",
                          borderRadius: 8,
                          borderColor: isDefault
                            ? OLIVEEB_COLORS.oliveebGreen
                            : OLIVEEB_COLORS.border,
                          background: isDefault ? OLIVEEB_COLORS.greenSoft2 : OLIVEEB_COLORS.card,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Text strong style={{ display: "block", wordBreak: "break-word" }}>
                              {maskedMethod(method)}
                            </Text>
                            <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                              <Tag color="blue">{readable(method?.status, "Status")}</Tag>
                              <Tag color="cyan">Exp {expiry(method)}</Tag>
                              {isDefault ? <Tag color="green">Default outgoing</Tag> : null}
                            </Space>
                          </div>
                          <Button
                            type={isDefault ? "default" : "primary"}
                            icon={isDefault ? <CheckCircleOutlined /> : <CreditCardOutlined />}
                            disabled={isDefault}
                            loading={defaultingId === id}
                            onClick={() => setDefaultOutgoing(method)}
                          >
                            {isDefault ? "Default" : "Set default"}
                          </Button>
                        </div>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No outgoing payment methods found for this ${context.type === "business" ? "organization" : "user"}`}
              />
            )}
          </Card>
          )}

          <Modal
            title="Add Outgoing Payment Method"
            open={addOpen}
            onCancel={closeAddPaymentMethod}
            okText="Add Payment Method"
            okButtonProps={{
              loading: addingMethod,
              disabled: !stripeReady || !cardComplete || Boolean(stripeError),
              style: {
                borderColor: OLIVEEB_COLORS.oliveebGreen,
                background: OLIVEEB_COLORS.oliveebGreen,
              },
            }}
            onOk={addPaymentMethod}
          >
            <Alert
              type={stripePublishableKey ? "info" : "warning"}
              showIcon
              message={`Add a card payment method to this ${context.type === "business" ? "organization" : "user"}.`}
              description={
                stripePublishableKey
                  ? "Enter card details securely through Stripe. Olreadia only saves the Stripe payment method reference."
                  : "Stripe publishable key is not configured for this environment."
              }
              style={{ marginBottom: 16 }}
            />

            <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
              Card Details
            </Text>
            <div
              style={{
                marginTop: 8,
                padding: "15px 16px",
                border: `1px solid ${stripeError ? OLIVEEB_COLORS.oliveebRed : OLIVEEB_COLORS.oliveebGreen}`,
                borderRadius: 16,
                background: OLIVEEB_COLORS.card,
                boxShadow: "0 16px 34px rgba(15, 23, 42, 0.12)",
              }}
            >
              <div ref={cardMountRef} />
              {!stripeReady && stripePublishableKey ? (
                <Text type="secondary">Loading secure card field...</Text>
              ) : null}
            </div>
            {stripeError ? (
              <Text type="danger" style={{ display: "block", marginTop: 8 }}>
                {stripeError}
              </Text>
            ) : (
              <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                Use the same secure Stripe card flow as Olreadia.
              </Text>
            )}
          </Modal>

        </Space>
      )}
    </div>
  );
};

export default WorkspacePaymentMethodsPage;
